import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { api } from '../../services/api';
import { PoS as Customer } from '../../types';
import { AddressForm } from '../shared/AddressForm';
import { DistributorSelector } from './DistributorSelector';
import {
  hasStoredInvoiceAddress,
  InvoiceBillingSection,
  invoiceAddressPayload,
} from './InvoiceBillingSection';
import { StoreTypeSelector } from '../visits/StoreTypeSelector';
import { DistrictAutocomplete } from '../shared/DistrictAutocomplete';

const customerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  store_type: z.string().min(1, 'Store type is required'),
  street: z.string().min(1, 'Street is required'),
  street_number: z.string().min(1, 'Street number is required'),
  postal_code: z.string().min(4, 'Postal code is required'),
  city: z.string().min(1, 'City is required'),
  country: z.string().optional(),
  district: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  invoice_email: z.string().email().optional().or(z.literal('')),
  owner_name: z.string().optional(),
  invoice_name: z.string().optional(),
  invoice_street: z.string().optional(),
  invoice_street_number: z.string().optional(),
  invoice_postal_code: z.string().optional(),
  invoice_city: z.string().optional(),
  invoice_country: z.string().optional(),
  contact_person_name: z.string().optional(),
  contact_person_phone: z.string().optional(),
  contact_person_email: z.string().email().optional().or(z.literal('')),
  contact_availability_time: z.string().optional(),
  estimated_traffic: z.string().optional(),
  notes: z.string().optional(),
  tax_number: z.string().max(50).optional(),
  vat_id: z.string().max(50).optional(),
  is_premium: z.boolean().optional().default(false),
});

type CustomerFormValues = z.infer<typeof customerSchema>;

interface DistributorLink {
  distributor_id: string;
  pos_number_at_distributor: string;
  is_primary: boolean;
}

export const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [distributorLinks, setDistributorLinks] = useState<DistributorLink[]>([]);
  const [initialDistributorLinks, setInitialDistributorLinks] = useState<DistributorLink[]>([]);
  const [initialDifferentAddress, setInitialDifferentAddress] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      store_type: 'spaeti',
      city: 'Berlin',
      country: 'Germany',
      invoice_country: '',
      is_premium: false,
    },
  });

  const isPremium = watch('is_premium');

  const city = watch('city');
  const district = watch('district');

  useEffect(() => {
    if (isEditMode) {
      const fetchCustomer = async () => {
        try {
          const response = await api.get(`/pos/${id}`);
          const customer: any = response.data;
          
          setValue('name', customer.name);
          setValue('store_type', customer.store_type);
          setValue('street', customer.street || '');
          setValue('street_number', customer.street_number || '');
          setValue('postal_code', customer.postal_code || '');
          setValue('city', customer.city);
          setValue('country', customer.country || 'Germany');
          setValue('district', customer.district || '');
          setValue('phone', customer.phone || '');
          setValue('email', customer.email || '');
          setValue('invoice_email', customer.invoice_email || '');
          setValue('owner_name', customer.owner_name || '');
          setValue('invoice_name', customer.invoice_name || '');
          setValue('invoice_street', customer.invoice_street || '');
          setValue('invoice_street_number', customer.invoice_street_number || '');
          setValue('invoice_postal_code', customer.invoice_postal_code || '');
          setValue('invoice_city', customer.invoice_city || '');
          setValue('invoice_country', customer.invoice_country || '');
          setValue('contact_person_name', customer.contact_person_name || '');
          setValue('contact_person_phone', customer.contact_person_phone || '');
          setValue('contact_person_email', customer.contact_person_email || '');
          setValue('contact_availability_time', customer.contact_availability_time || '');
          setValue('estimated_traffic', customer.estimated_traffic || '');
          setValue('notes', customer.notes || '');
          setValue('tax_number', customer.tax_number || '');
          setValue('vat_id', customer.vat_id || '');
          setValue('is_premium', customer.is_premium ?? false);
          setInitialDifferentAddress(hasStoredInvoiceAddress(customer));

          // Fetch distributor links
          const distRes = await api.get(`/pos/${id}/distributors`);
          const loadedLinks: DistributorLink[] = distRes.data.map((d: {
            distributor_id: string;
            pos_number_at_distributor: string;
            is_primary: boolean;
          }) => ({
            distributor_id: d.distributor_id,
            pos_number_at_distributor: d.pos_number_at_distributor ?? '',
            is_primary: d.is_primary ?? false,
          }));
          setInitialDistributorLinks(loadedLinks);
          setDistributorLinks(loadedLinks);
        } catch (error) {
          console.error('Failed to fetch customer:', error);
          navigate('/pos');
        }
      };
      fetchCustomer();
    }
  }, [id, isEditMode, setValue, navigate]);

  const onSubmit = async (data: CustomerFormValues) => {
    try {
      const payload = {
        name: data.name,
        store_type: data.store_type,
        street: data.street,
        street_number: data.street_number,
        postal_code: data.postal_code,
        city: data.city,
        country: data.country || 'Germany',
        district: data.district || undefined,
        phone: data.phone || undefined,
        email: data.email || undefined,
        invoice_email: data.invoice_email || undefined,
        owner_name: data.owner_name || undefined,
        ...invoiceAddressPayload(data),
        contact_person_name: data.contact_person_name || undefined,
        contact_person_phone: data.contact_person_phone || undefined,
        contact_person_email: data.contact_person_email || undefined,
        contact_availability_time: data.contact_availability_time || undefined,
        estimated_traffic: data.estimated_traffic || undefined,
        notes: data.notes || undefined,
        tax_number: data.tax_number || undefined,
        vat_id: data.vat_id || undefined,
        is_premium: data.is_premium ?? false,
      };

      let customerId = id;

      if (isEditMode) {
        await api.put(`/pos/${id}`, payload);
      } else {
        const createRes = await api.post('/pos', payload);
        customerId = createRes.data.id;
      }

      if (customerId) {
        for (const link of distributorLinks) {
          await api.post('/distributors/links', {
            pos_id: customerId,
            distributor_id: link.distributor_id,
            pos_number_at_distributor: link.pos_number_at_distributor || '',
            is_primary: link.is_primary,
          });
        }

        if (isEditMode) {
          const currentIds = new Set(distributorLinks.map((l) => l.distributor_id));
          for (const link of initialDistributorLinks) {
            if (!currentIds.has(link.distributor_id)) {
              await api.delete(`/distributors/links/${customerId}/${link.distributor_id}`);
            }
          }
        }
      }

      navigate('/pos');
    } catch (error: any) {
      console.error('Failed to save customer:', error);
      toast.error(error.response?.data?.error || 'Failed to save customer');
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-3xl font-black text-gray-900 mb-6">
        {isEditMode ? 'Edit Store' : 'New Store'}
      </h1>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-white p-6 rounded-2xl shadow-lg">
        <div>
          <label
            htmlFor="pos-name"
            className="block text-sm font-semibold text-gray-700 mb-1.5"
          >
            Store Name <span className="text-red-500">*</span>
          </label>
          <input
            id="pos-name"
            type="text"
            {...register('name')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            placeholder="e.g. Corner Shop"
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
        </div>

        <div>
          <StoreTypeSelector
            value={watch('store_type')}
            onChange={(value) => setValue('store_type', value)}
          />
          {errors.store_type && <p className="mt-1 text-sm text-red-600">{errors.store_type.message}</p>}
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Address</h3>
          <AddressForm 
            register={register}
            errors={errors}
            required={true}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            District/City Area
          </label>
          <DistrictAutocomplete
            value={district || ''}
            onChange={(value) => setValue('district', value)}
            city={city}
            placeholder="e.g. Kreuzberg, Mitte"
          />
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Phone
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="+49 30 12345678"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="kontakt@beispiel.de"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Contact Person Name
                </label>
                <input
                  type="text"
                  {...register('contact_person_name')}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="z.B. Max Mustermann"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Contact Person Phone
                </label>
                <input
                  type="tel"
                  {...register('contact_person_phone')}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="+49 170 1234567"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Contact Person Email
              </label>
              <input
                type="email"
                {...register('contact_person_email')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="max@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Contact Person Availability
              </label>
              <div className="flex gap-3">
                {[
                  { value: 'morning', label: 'Morning', emoji: '🌅' },
                  { value: 'afternoon', label: 'Afternoon', emoji: '☀️' },
                  { value: 'evening', label: 'Evening', emoji: '🌙' }
                ].map((time) => {
                  const currentValue = watch('contact_availability_time') || '';
                  const values = currentValue.split(',').filter(Boolean);
                  const isSelected = values.includes(time.value);
                  
                  return (
                    <button
                      key={time.value}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          const newValues = values.filter(v => v !== time.value);
                          setValue('contact_availability_time', newValues.join(','));
                        } else {
                          const newValues = [...values, time.value];
                          setValue('contact_availability_time', newValues.join(','));
                        }
                      }}
                      className={`flex-1 min-h-[56px] px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                        isSelected
                          ? 'bg-blue-500 text-white shadow-md border-2 border-blue-600'
                          : 'bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className="text-xl mb-1">{time.emoji}</div>
                      {time.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">Multiple selections allowed</p>
            </div>
          </div>
        </div>

        <InvoiceBillingSection
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          initialDifferentAddress={initialDifferentAddress}
        />

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Distributors</h3>
          <DistributorSelector
            value={distributorLinks}
            onChange={setDistributorLinks}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Estimated Customer Traffic
          </label>
          <select
            {...register('estimated_traffic')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            <option value="">-- Select --</option>
            <option value="low">Low (&lt; 50/day)</option>
            <option value="medium">Medium (50-200/day)</option>
            <option value="high">High (200-500/day)</option>
            <option value="very_high">Very High (&gt; 500/day)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Notes
          </label>
          <textarea
            {...register('notes')}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
            placeholder="Additional information..."
          />
          <p className="mt-1.5 text-xs text-gray-500">
            Saved as a pinned note on the PoS. Further notes are added on the PoS page, so
            nothing gets overwritten.
          </p>
        </div>

        {/* Premium toggle */}
        <div className="border border-gray-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Premium Location</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Flag this PoS as a priority location for sampling and first placements.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setValue('is_premium', !isPremium)}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-semibold text-sm transition-colors ${
              isPremium
                ? 'border-amber-400 bg-amber-50 text-amber-700'
                : 'border-gray-200 bg-white text-gray-500 hover:border-amber-300'
            }`}
          >
            <Star size={16} className={isPremium ? 'fill-amber-400 text-amber-400' : ''} />
            {isPremium ? 'Premium' : 'Standard'}
          </button>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/pos')}
            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (isEditMode ? 'Update' : 'Create Store')}
          </button>
        </div>
      </form>
    </div>
  );
};
